/**
 * KS2-I1 AddSourceModal
 * Four-stage controlled ingestion workflow:
 *   Step 1 — Choose file (real file picker, client-side validation)
 *   Step 2 — Describe source (provenance metadata)
 *   Step 3 — Fingerprinting (upload + SHA-256 display)
 *   Step 4 — Confirmation
 */
import { useState, useRef, useCallback } from 'react';
import { X, Upload, FileText, FileCheck, AlertTriangle, Copy, Check, ChevronRight, Loader } from 'lucide-react';
import api from '@/lib/api';
import { session } from '@/lib/session';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ALLOWED_TYPES = {
  'application/pdf': '.pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'text/plain': '.txt',
  'text/markdown': '.md',
  'text/x-markdown': '.md',
};

const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.md'];
const MAX_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB

const SOURCE_TYPES = [
  'Policy Document',
  'Procedure',
  'Training Material',
  'Addendum',
  'Guideline',
  'Standard',
  'Reference',
  'Other',
];

const ADMISSION_BADGE = {
  CANDIDATE:               { bg: '#e8fdf1', color: '#027a48', label: 'CANDIDATE' },
  SOURCE_REQUIRED:         { bg: '#fff4e5', color: '#b45309', label: 'SOURCE REQUIRED' },
  LOW_CONFIDENCE:          { bg: '#fef9e7', color: '#b45309', label: 'LOW CONFIDENCE' },
  QUARANTINED:             { bg: '#fee2e2', color: '#b91c1c', label: 'QUARANTINED' },
  READY_FOR_HUMAN_REVIEW:  { bg: '#eff6ff', color: '#1d4ed8', label: 'READY FOR REVIEW' },
  AWAITING_SECOND_REVIEW:  { bg: '#eff6ff', color: '#1d4ed8', label: 'AWAITING 2ND REVIEW' },
  CANON_LOCKED:            { bg: '#000000', color: '#03f37c', label: 'CANON LOCKED' },
  SAFE_HOLD:               { bg: '#f5f3ff', color: '#7c3aed', label: 'SAFE HOLD' },
  SUPERSEDED:              { bg: '#f5f5f5', color: '#737373', label: 'SUPERSEDED' },
};

// ─── Helpers ────────────────────────────────────────────────────────────────

function truncSha(sha) {
  if (!sha) return '—';
  return `${sha.slice(0, 8)}…${sha.slice(-8)}`;
}

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function validateFile(file) {
  if (!file) return 'No file selected.';
  const ext = '.' + file.name.split('.').pop().toLowerCase();
  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    return `Unsupported file type "${ext}". Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`;
  }
  const mimeOk = Object.keys(ALLOWED_TYPES).includes(file.type) ||
    file.type === '' /* some OS don't set MIME for .md */;
  if (!mimeOk && file.type) {
    return `MIME type "${file.type}" is not accepted.`;
  }
  if (file.size > MAX_SIZE_BYTES) {
    return `File is ${fmtSize(file.size)}, exceeding the 25 MB limit.`;
  }
  if (file.name.includes('..') || file.name.includes('/') || file.name.includes('\\')) {
    return 'Filename contains illegal path characters.';
  }
  return null;
}

// ─── Admission badge ────────────────────────────────────────────────────────

export function AdmissionBadge({ state }) {
  const style = ADMISSION_BADGE[state];
  if (!style) return null;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded font-heading font-semibold"
      style={{
        backgroundColor: style.bg,
        color: style.color,
        fontSize: '10px',
        letterSpacing: '0.06em',
        border: state === 'CANON_LOCKED' ? '1px solid rgba(3,243,124,0.4)' : `1px solid ${style.color}22`,
      }}
      data-testid={`admission-badge-${state}`}
    >
      {style.label}
    </span>
  );
}

// ─── Step indicators ─────────────────────────────────────────────────────────

function Steps({ current }) {
  const steps = ['File', 'Describe', 'Fingerprint', 'Confirm'];
  return (
    <div className="flex items-center gap-0 mb-8" data-testid="modal-steps">
      {steps.map((s, i) => {
        const idx = i + 1;
        const done = idx < current;
        const active = idx === current;
        return (
          <div key={s} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center font-heading font-semibold"
                style={{
                  fontSize: '12px',
                  backgroundColor: done ? '#000' : active ? '#000' : '#f5f5f5',
                  color: done ? '#03f37c' : active ? '#fff' : '#a3a3a3',
                  border: active ? '2px solid #000' : done ? '2px solid #000' : '2px solid #e5e5e5',
                }}
                data-testid={`step-indicator-${idx}`}
              >
                {done ? <Check size={12} /> : idx}
              </div>
              <span
                className="mt-1 font-heading"
                style={{ fontSize: '10px', color: active ? '#000' : '#a3a3a3', letterSpacing: '0.05em' }}
              >
                {s}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className="mx-2 mb-4"
                style={{ width: '32px', height: '1px', backgroundColor: idx < current ? '#000' : '#e5e5e5' }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Step 1 — Choose file ───────────────────────────────────────────────────

function Step1({ file, setFile, fileError, setFileError, onNext }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleFile = useCallback((f) => {
    const err = validateFile(f);
    setFileError(err);
    if (!err) setFile(f);
  }, [setFile, setFileError]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  }, [handleFile]);

  return (
    <div data-testid="step-1-choose-file">
      <h2 className="font-heading font-semibold text-black mb-1" style={{ fontSize: '17px' }}>
        Choose file
      </h2>
      <p className="font-body text-gray-500 mb-5" style={{ fontSize: '13px' }}>
        PDF, DOCX, TXT, or Markdown. Maximum 25 MB.
      </p>

      {/* Drop zone */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className="relative flex flex-col items-center justify-center rounded border-2 border-dashed transition-colors cursor-pointer"
        style={{
          padding: '40px 24px',
          borderColor: dragging ? '#000' : file ? '#03f37c' : '#d9dee6',
          backgroundColor: dragging ? '#f9fafb' : file ? '#f0fdf6' : '#fafafa',
          minHeight: '140px',
        }}
        data-testid="file-drop-zone"
        aria-label="File drop zone — click or drag a file here"
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.docx,.txt,.md"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
          className="sr-only"
          data-testid="file-input"
          aria-label="File input"
        />
        {file ? (
          <>
            <FileCheck size={28} style={{ color: '#03f37c', marginBottom: '8px' }} />
            <p className="font-heading font-semibold text-black" style={{ fontSize: '14px' }}>
              {file.name}
            </p>
            <p className="font-body text-gray-500 mt-1" style={{ fontSize: '12px' }}>
              {fmtSize(file.size)}
            </p>
          </>
        ) : (
          <>
            <Upload size={24} style={{ color: '#a3a3a3', marginBottom: '8px' }} />
            <p className="font-heading font-medium text-black" style={{ fontSize: '14px' }}>
              Drop file here or click to browse
            </p>
            <p className="font-body text-gray-400 mt-1" style={{ fontSize: '12px' }}>
              PDF · DOCX · TXT · MD — max 25 MB
            </p>
          </>
        )}
      </div>

      {fileError && (
        <div
          className="mt-3 flex items-start gap-2 rounded p-3"
          style={{ backgroundColor: '#fee2e2' }}
          data-testid="file-error"
        >
          <AlertTriangle size={14} style={{ color: '#b91c1c', marginTop: '1px', flexShrink: 0 }} />
          <span className="font-body" style={{ fontSize: '12px', color: '#b91c1c' }}>{fileError}</span>
        </div>
      )}

      <div className="flex justify-end mt-6">
        <button
          onClick={onNext}
          disabled={!file || !!fileError}
          className="font-heading font-semibold px-5 py-2.5 rounded transition-colors"
          style={{
            backgroundColor: file && !fileError ? '#000' : '#e5e5e5',
            color: file && !fileError ? '#fff' : '#a3a3a3',
            fontSize: '13px',
          }}
          data-testid="step1-next-btn"
        >
          Next <ChevronRight size={14} style={{ display: 'inline', marginLeft: '2px' }} />
        </button>
      </div>
    </div>
  );
}

// ─── Step 2 — Describe source ────────────────────────────────────────────────

function Step2({ form, setForm, onNext, onBack }) {
  const valid = form.title.trim() && form.source_type && form.owner.trim();

  return (
    <div data-testid="step-2-describe">
      <h2 className="font-heading font-semibold text-black mb-1" style={{ fontSize: '17px' }}>
        Describe source
      </h2>
      <p className="font-body text-gray-500 mb-5" style={{ fontSize: '13px' }}>
        Provenance metadata is required to establish canon eligibility.
      </p>

      <div className="space-y-4">
        <div>
          <label className="font-heading font-medium text-black block mb-1" style={{ fontSize: '12px', letterSpacing: '0.04em' }}>
            TITLE <span style={{ color: '#b91c1c' }}>*</span>
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="e.g. Incident Response Procedure v3.1"
            className="w-full rounded border px-3 py-2 font-body"
            style={{ fontSize: '13px', borderColor: '#d9dee6', outline: 'none' }}
            data-testid="input-title"
          />
        </div>

        <div>
          <label className="font-heading font-medium text-black block mb-1" style={{ fontSize: '12px', letterSpacing: '0.04em' }}>
            SOURCE TYPE <span style={{ color: '#b91c1c' }}>*</span>
          </label>
          <select
            value={form.source_type}
            onChange={(e) => setForm(f => ({ ...f, source_type: e.target.value }))}
            className="w-full rounded border px-3 py-2 font-body bg-white"
            style={{ fontSize: '13px', borderColor: '#d9dee6', outline: 'none' }}
            data-testid="select-source-type"
          >
            <option value="">Select type…</option>
            {SOURCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div>
          <label className="font-heading font-medium text-black block mb-1" style={{ fontSize: '12px', letterSpacing: '0.04em' }}>
            OWNER <span style={{ color: '#b91c1c' }}>*</span>
          </label>
          <input
            type="text"
            value={form.owner}
            onChange={(e) => setForm(f => ({ ...f, owner: e.target.value }))}
            placeholder="e.g. Dr. Priya Anand"
            className="w-full rounded border px-3 py-2 font-body"
            style={{ fontSize: '13px', borderColor: '#d9dee6', outline: 'none' }}
            data-testid="input-owner"
          />
        </div>

        <div>
          <label className="font-heading font-medium text-black block mb-1" style={{ fontSize: '12px', letterSpacing: '0.04em' }}>
            DESCRIPTION
          </label>
          <textarea
            rows={3}
            value={form.description}
            onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="Brief description of this source's purpose and scope…"
            className="w-full rounded border px-3 py-2 font-body resize-none"
            style={{ fontSize: '13px', borderColor: '#d9dee6', outline: 'none' }}
            data-testid="input-description"
          />
        </div>
      </div>

      <div className="flex justify-between mt-6">
        <button
          onClick={onBack}
          className="font-heading font-medium px-4 py-2 rounded border transition-colors"
          style={{ fontSize: '13px', borderColor: '#d9dee6', color: '#525252' }}
          data-testid="step2-back-btn"
        >
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!valid}
          className="font-heading font-semibold px-5 py-2.5 rounded transition-colors"
          style={{
            backgroundColor: valid ? '#000' : '#e5e5e5',
            color: valid ? '#fff' : '#a3a3a3',
            fontSize: '13px',
          }}
          data-testid="step2-next-btn"
        >
          Next <ChevronRight size={14} style={{ display: 'inline', marginLeft: '2px' }} />
        </button>
      </div>
    </div>
  );
}

// ─── Step 3 — Fingerprinting ─────────────────────────────────────────────────

function Step3({ file, form, onDone, onBack }) {
  const [phase, setPhase] = useState('idle'); // idle | uploading | done | error
  const [sha256, setSha256] = useState(null);
  const [artifactId, setArtifactId] = useState(null);
  const [admissionState, setAdmissionState] = useState(null);
  const [passageCount, setPassageCount] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revealFull, setRevealFull] = useState(false);

  const startUpload = useCallback(async () => {
    setPhase('uploading');
    setErrorMsg(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', form.title);
    formData.append('source_type', form.source_type);
    formData.append('owner', form.owner);
    formData.append('description', form.description || '');
    formData.append('submitted_by', session.getUser()?.display_name ?? 'Anonymous');

    try {
      const csrf = session.getCsrfToken();
      const headers = {};
      if (csrf) headers['X-CSRF-Token'] = csrf;

      const res = await fetch(`${BACKEND_URL}/api/sources/upload`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || `Upload failed (${res.status})`);
      }

      setSha256(data.sha256);
      setArtifactId(data.id);
      setAdmissionState(data.admission_state);
      setPassageCount(data.passage_count ?? 0);
      setPhase('done');
    } catch (err) {
      setErrorMsg(err.message || 'Upload failed.');
      setPhase('error');
    }
  }, [file, form]);

  const copyHash = () => {
    navigator.clipboard.writeText(sha256).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div data-testid="step-3-fingerprint">
      <h2 className="font-heading font-semibold text-black mb-1" style={{ fontSize: '17px' }}>
        Source fingerprint
      </h2>
      <p className="font-body text-gray-500 mb-5" style={{ fontSize: '13px' }}>
        Content is uploaded, extracted, and SHA-256 fingerprinted server-side.
        The client never supplies the hash.
      </p>

      {phase === 'idle' && (
        <div className="text-center py-6">
          <FileText size={32} style={{ color: '#a3a3a3', margin: '0 auto 12px' }} />
          <p className="font-body text-gray-600 mb-4" style={{ fontSize: '13px' }}>
            Ready to fingerprint: <strong>{file.name}</strong> ({fmtSize(file.size)})
          </p>
          <button
            onClick={startUpload}
            className="font-heading font-semibold px-6 py-2.5 rounded transition-colors"
            style={{ backgroundColor: '#000', color: '#fff', fontSize: '13px' }}
            data-testid="start-fingerprint-btn"
          >
            Create source fingerprint
          </button>
        </div>
      )}

      {phase === 'uploading' && (
        <div className="text-center py-8" data-testid="fingerprint-loading">
          <Loader size={28} className="animate-spin" style={{ color: '#000', margin: '0 auto 12px' }} />
          <p className="font-heading font-semibold text-black" style={{ fontSize: '14px' }}>
            Creating source fingerprint…
          </p>
          <p className="font-body text-gray-400 mt-1" style={{ fontSize: '12px' }}>
            Extracting content · Computing SHA-256 · Assessing admission state
          </p>
        </div>
      )}

      {phase === 'error' && (
        <div
          className="rounded p-4 flex gap-3"
          style={{ backgroundColor: '#fee2e2' }}
          data-testid="fingerprint-error"
        >
          <AlertTriangle size={16} style={{ color: '#b91c1c', flexShrink: 0, marginTop: '1px' }} />
          <div>
            <p className="font-heading font-semibold" style={{ color: '#b91c1c', fontSize: '13px' }}>
              Ingestion failed
            </p>
            <p className="font-body mt-0.5" style={{ color: '#991b1b', fontSize: '12px' }}>{errorMsg}</p>
          </div>
        </div>
      )}

      {phase === 'done' && sha256 && (
        <div data-testid="fingerprint-result">
          {/* SHA-256 display */}
          <div
            className="rounded p-4 mb-4"
            style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e5e5' }}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-heading font-semibold text-black" style={{ fontSize: '11px', letterSpacing: '0.06em' }}>
                SHA-256 FINGERPRINT
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setRevealFull(r => !r)}
                  className="font-heading text-gray-500 hover:text-black transition-colors"
                  style={{ fontSize: '11px' }}
                  data-testid="reveal-full-hash-btn"
                >
                  {revealFull ? 'Abbreviate' : 'Reveal full'}
                </button>
                <button
                  onClick={copyHash}
                  className="flex items-center gap-1 font-heading text-gray-500 hover:text-black transition-colors"
                  style={{ fontSize: '11px' }}
                  data-testid="copy-hash-btn"
                  aria-label="Copy SHA-256"
                >
                  {copied ? <Check size={12} style={{ color: '#03f37c' }} /> : <Copy size={12} />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
            <code
              className="font-mono block"
              style={{
                fontSize: revealFull ? '10px' : '13px',
                color: '#000',
                wordBreak: 'break-all',
                letterSpacing: revealFull ? '0.02em' : '0.04em',
              }}
              data-testid="sha256-display"
            >
              {revealFull ? sha256 : truncSha(sha256)}
            </code>
          </div>

          {/* Admission state */}
          <div className="flex items-center justify-between rounded p-3 mb-4"
            style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e5e5' }}
          >
            <span className="font-heading font-medium text-gray-600" style={{ fontSize: '12px' }}>
              Admission state
            </span>
            <AdmissionBadge state={admissionState} />
          </div>

          {/* Passage count */}
          <div className="flex items-center justify-between rounded p-3 mb-2"
            style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e5e5' }}
          >
            <span className="font-heading font-medium text-gray-600" style={{ fontSize: '12px' }}>
              Passages extracted
            </span>
            <span className="font-heading font-semibold text-black" style={{ fontSize: '12px' }}
              data-testid="passage-count">
              {passageCount}
            </span>
          </div>
        </div>
      )}

      <div className="flex justify-between mt-6">
        <button
          onClick={onBack}
          disabled={phase === 'uploading'}
          className="font-heading font-medium px-4 py-2 rounded border transition-colors"
          style={{ fontSize: '13px', borderColor: '#d9dee6', color: '#525252' }}
          data-testid="step3-back-btn"
        >
          Back
        </button>
        {phase === 'done' && (
          <button
            onClick={() => onDone({ sha256, artifactId, admissionState, passageCount })}
            className="font-heading font-semibold px-5 py-2.5 rounded"
            style={{ backgroundColor: '#000', color: '#fff', fontSize: '13px' }}
            data-testid="step3-confirm-btn"
          >
            Confirm submission
          </button>
        )}
        {phase === 'error' && (
          <button
            onClick={() => setPhase('idle')}
            className="font-heading font-semibold px-5 py-2.5 rounded border"
            style={{ fontSize: '13px', borderColor: '#000', color: '#000' }}
            data-testid="step3-retry-btn"
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Step 4 — Confirmation ────────────────────────────────────────────────────

function Step4({ file, form, result, onClose }) {
  return (
    <div data-testid="step-4-confirm">
      <div className="text-center mb-6">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
          style={{ backgroundColor: '#000' }}
        >
          <Check size={22} style={{ color: '#03f37c' }} />
        </div>
        <h2 className="font-heading font-semibold text-black" style={{ fontSize: '17px' }}>
          Source ingested
        </h2>
        <p className="font-body text-gray-500 mt-1" style={{ fontSize: '13px' }}>
          {form.title} has entered the governed review queue.
        </p>
      </div>

      <div className="rounded p-4 space-y-3 mb-6" style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e5e5' }}>
        <Row label="Title" value={form.title} />
        <Row label="Owner" value={form.owner} />
        <Row label="Type" value={form.source_type} />
        <Row label="File" value={file.name} />
        <Row label="SHA-256" value={<code style={{ fontSize: '11px', wordBreak: 'break-all' }}>{truncSha(result.sha256)}</code>} />
        <div className="flex items-center justify-between">
          <span className="font-heading font-medium text-gray-500" style={{ fontSize: '12px', letterSpacing: '0.04em' }}>
            ADMISSION STATE
          </span>
          <AdmissionBadge state={result.admissionState} />
        </div>
      </div>

      <p className="font-body text-gray-500 text-center mb-5" style={{ fontSize: '12px' }}>
        This artifact now requires two distinct human approvals before canon lock.
        No AI model determined this admission — a human authority must validate.
      </p>

      <div className="flex justify-center">
        <button
          onClick={onClose}
          className="font-heading font-semibold px-6 py-2.5 rounded"
          style={{ backgroundColor: '#000', color: '#fff', fontSize: '13px' }}
          data-testid="done-btn"
        >
          Done
        </button>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="font-heading font-medium text-gray-500 shrink-0" style={{ fontSize: '12px', letterSpacing: '0.04em' }}>
        {label.toUpperCase()}
      </span>
      <span className="font-body text-black text-right" style={{ fontSize: '12px' }}>
        {value}
      </span>
    </div>
  );
}

// ─── Modal shell ─────────────────────────────────────────────────────────────

export default function AddSourceModal({ onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [form, setForm] = useState({
    title: '',
    source_type: '',
    owner: '',
    description: '',
  });
  const [result, setResult] = useState(null);

  const handleFingerprint = useCallback((res) => {
    setResult(res);
    setStep(4);
    if (onSuccess) onSuccess(res);
  }, [onSuccess]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      data-testid="add-source-modal-overlay"
    >
      <div
        className="bg-white rounded-lg w-full relative"
        style={{ maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}
        data-testid="add-source-modal"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 pt-5 pb-4"
          style={{ borderBottom: '1px solid #e5e5e5' }}
        >
          <span className="font-heading font-semibold text-black" style={{ fontSize: '15px' }}>
            Add source
          </span>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-black transition-colors"
            aria-label="Close"
            data-testid="modal-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <Steps current={step} />

          {step === 1 && (
            <Step1
              file={file}
              setFile={setFile}
              fileError={fileError}
              setFileError={setFileError}
              onNext={() => setStep(2)}
            />
          )}
          {step === 2 && (
            <Step2
              form={form}
              setForm={setForm}
              onNext={() => setStep(3)}
              onBack={() => setStep(1)}
            />
          )}
          {step === 3 && (
            <Step3
              file={file}
              form={form}
              onDone={handleFingerprint}
              onBack={() => setStep(2)}
            />
          )}
          {step === 4 && result && (
            <Step4
              file={file}
              form={form}
              result={result}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
}
